import type { Explanation } from '../../explanations/types';
import type { Translate } from '../../explanations/common';
import { MAX_ANEXOS_POR_MENSAGEM } from '../../composables/useChamados';

/**
 * The tickets domain explains itself from here (skill `self-explaining-ui`,
 * E5: the fixed text lives in the catalog, never inline in the template). Only
 * the dynamic `now` — why a control is blocked right now — is built next to
 * the rule that blocks it, in ChamadosModal.
 *
 * The worked example of docs/plans/explicacoes-na-tela.md, control by control.
 * The backend still decides what is allowed (`transicoes_possiveis`,
 * `pedidos_de_agente`); these sentences only describe it.
 */

/** What each status means, and its icon on the stepper card. */
export const STATUS_MEANING: Record<string, { icon: string; text: string }> = {
  aberto: { icon: 'pi pi-inbox', text: 'Chamado recebido, ainda sem ninguém atendendo.' },
  em_atendimento: { icon: 'pi pi-headphones', text: 'A equipe da franqueadora está trabalhando nele.' },
  aguardando_solicitante: { icon: 'pi pi-hourglass', text: 'A bola está com o solicitante: falta uma resposta ou informação dele.' },
  resolvido: { icon: 'pi pi-check-circle', text: 'A solução foi entregue. Se o solicitante responder, ele volta para atendimento.' },
  fechado: { icon: 'pi pi-lock', text: 'Encerrado de vez: não recebe mais mensagens nem muda de status.' },
  cancelado: { icon: 'pi pi-ban', text: 'Encerrado sem solução: duplicado, desistência ou fora do escopo.' },
};

/** The agent's effort scale — ChamadoEsforco in app-api. */
export const EFFORT_MEANING: Record<string, string> = {
  S: 'Até 3 horas',
  M: 'Meio dia a um dia',
  L: 'Dois a cinco dias',
  XL: 'Mais de uma semana — dividir',
};

export interface TicketExplanations {
  seeScreen: Explanation;
  plan: Explanation;
  resolve: Explanation;
  unmark: Explanation;
  effort: Explanation;
  cancelTicket: Explanation;
  refresh: Explanation;
  replyTab: Explanation;
  noteTab: Explanation;
  suggest: Explanation;
  send: Explanation;
  saveNote: Explanation;
  sending: Explanation;
  opening: Explanation;
  statusColumn: Explanation;
  tabs: Record<'em_andamento' | 'encerrados' | 'todos', Explanation>;
  removeAttachment: Explanation;
  attachmentLimit: Explanation;
  uploadingLogo: Explanation;
}

export const ticketExplanations = (t: Translate): TicketExplanations => ({
  seeScreen: {
    icon: 'pi pi-external-link',
    what: t('Abre a tela em que o solicitante estava quando abriu o chamado.'),
    now: { kind: 'yes', text: t('Fecha este chamado e leva você até lá.') },
  },
  plan: {
    icon: 'pi pi-map',
    what: t('O agente lê o chamado e o código e escreve um plano, com o esforço estimado (S a XL).'),
    steps: [
      t('Marca o chamado'),
      t('O Steward pega na próxima rodada'),
      t('O plano chega aqui como nota interna'),
    ],
    now: { kind: 'info', text: t('Não muda o status nem responde ao solicitante.') },
  },
  resolve: {
    icon: 'pi pi-wrench',
    what: t('O agente implementa a correção, testa e entrega em homologação.'),
    steps: [
      t('Marca o chamado'),
      t('Implementa e testa'),
      t('Sobe em homologação e grava a versão'),
      t('Avisa aqui quando chegar em produção'),
    ],
    now: { kind: 'info', text: t('O solicitante só é avisado quando chegar em produção.') },
  },
  unmark: {
    what: t('Cancela o pedido ao agente antes de ele começar.'),
    now: { kind: 'info', text: t('Um trabalho já em andamento termina; só a marca sai.') },
  },
  effort: {
    icon: 'pi pi-gauge',
    what: t('Quanto trabalho o agente estimou ao planejar.'),
    steps: Object.entries(EFFORT_MEANING).map(([size, meaning]) => `${size} — ${t(meaning)}`),
    now: { kind: 'info', text: t('A estimativa é do agente; o plano completo está no fio.') },
  },
  cancelTicket: {
    icon: STATUS_MEANING.cancelado.icon,
    tone: 'danger',
    what: t(STATUS_MEANING.cancelado.text),
    now: { kind: 'info', text: t('Não tem volta: um chamado cancelado não recebe mais mensagens.') },
  },
  refresh: {
    what: t('Busca mensagens novas.'),
    now: { kind: 'info', text: t('A conversa não se atualiza sozinha enquanto o modal está aberto.') },
  },
  replyTab: {
    what: t('A mensagem vai para o solicitante, que recebe por e-mail.'),
  },
  noteTab: {
    tone: 'warn',
    what: t('Só a equipe vê. O solicitante não lê e não recebe e-mail.'),
  },
  suggest: {
    icon: 'pi pi-sparkles',
    what: t('A IA escreve um rascunho de resposta a partir do fio.'),
    now: { kind: 'info', text: t('Revise antes de enviar: ela não lê as notas internas.') },
  },
  send: {
    what: t('Envia ao solicitante, que recebe por e-mail.'),
    now: { kind: 'info', text: t('Enter também envia; Shift+Enter quebra a linha.') },
  },
  saveNote: {
    tone: 'warn',
    what: t('Salva a nota para a equipe — o solicitante não vê.'),
    now: { kind: 'info', text: t('Enter também salva; Shift+Enter quebra a linha.') },
  },
  sending: {
    what: t('Enviando a mensagem anterior.'),
    now: { kind: 'no', text: t('Aguarde terminar.') },
  },
  opening: {
    what: t('Abrindo o chamado.'),
    now: { kind: 'no', text: t('Aguarde terminar.') },
  },
  statusColumn: {
    what: t('Em que ponto do atendimento o chamado está.'),
    steps: [
      t('Aberto'),
      t('Em atendimento'),
      t('Aguardando resposta ou Resolvido'),
      t('Fechado — ou Cancelado, sem solução'),
    ],
    now: { kind: 'info', text: t('Passe o mouse no status de uma linha para ver o que ele quer dizer.') },
  },
  tabs: {
    em_andamento: { what: t('Chamados abertos, em atendimento ou aguardando resposta.') },
    encerrados: { what: t('Chamados resolvidos, fechados ou cancelados.') },
    todos: { what: t('Todos os chamados, em qualquer status.') },
  },
  removeAttachment: {
    what: t('Tira esta imagem da mensagem.'),
    now: { kind: 'info', text: t('Ela ainda não foi enviada: nada sai do chamado.') },
  },
  attachmentLimit: {
    what: `${t('Limite de imagens por mensagem')}: ${MAX_ANEXOS_POR_MENSAGEM}.`,
    now: { kind: 'no', text: t('Remova uma imagem para anexar outra.') },
  },
  uploadingLogo: {
    what: t('Enviando o logo novo.'),
    now: { kind: 'no', text: t('Aguarde terminar.') },
  },
});
